import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  Shield, 
  Cookie, 
  Scale,
  ExternalLink,
  Save,
  RotateCcw,
  Loader2,
  Check,
  Edit3,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LegalPagesEditor = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('privacy-policy');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [pagesData, setPagesData] = useState({
    'privacy-policy': { title: 'Privacy Policy', content: '', last_updated: null },
    'terms-of-service': { title: 'Terms of Service', content: '', last_updated: null },
    'cookie-policy': { title: 'Cookie Policy', content: '', last_updated: null }
  });
  const [editedContent, setEditedContent] = useState('');

  const pages = [
    {
      id: 'privacy-policy',
      name: 'Privacy Policy',
      icon: Shield,
      path: '/privacy-policy',
      description: 'Explains how user data is collected, used, and protected',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      id: 'terms-of-service',
      name: 'Terms of Service',
      icon: Scale,
      path: '/terms-of-service',
      description: 'Defines the rules and guidelines for using the platform',
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 'cookie-policy',
      name: 'Cookie Policy',
      icon: Cookie,
      path: '/cookie-policy',
      description: 'Details how cookies and tracking technologies are used',
      color: 'from-amber-500 to-orange-500'
    }
  ];

  useEffect(() => {
    fetchLegalPage(activeTab);
  }, [activeTab]);

  const fetchLegalPage = async (pageId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/cms/legal/${pageId}`);
      if (response.ok) {
        const data = await response.json();
        setPagesData(prev => ({
          ...prev,
          [pageId]: data
        }));
        setEditedContent(data.content || '');
      }
    } catch (error) {
      console.error('Error fetching legal page:', error);
      toast.error('Failed to load page content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('examapp_token');
    
    try {
      const response = await fetch(`${API_URL}/api/cms/legal/${activeTab}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: pagesData[activeTab].title,
          content: editedContent
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPagesData(prev => ({
          ...prev,
          [activeTab]: data.page
        }));
        setEditMode(false);
        toast.success('Page saved successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save page');
      }
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset this page to the default content? This cannot be undone.')) {
      return;
    }

    setResetting(true);
    const token = localStorage.getItem('examapp_token');

    try {
      const response = await fetch(`${API_URL}/api/cms/legal/${activeTab}/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPagesData(prev => ({
          ...prev,
          [activeTab]: data.page
        }));
        setEditedContent(data.page.content || '');
        setEditMode(false);
        toast.success('Page reset to defaults');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset page');
      }
    } catch (error) {
      console.error('Error resetting page:', error);
      toast.error('Failed to reset page');
    } finally {
      setResetting(false);
    }
  };

  const handleViewPage = (path) => {
    window.open(path, '_blank');
  };

  const handleTabChange = (pageId) => {
    if (editMode && editedContent !== pagesData[activeTab].content) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to switch tabs?')) {
        return;
      }
    }
    setActiveTab(pageId);
    setEditMode(false);
  };

  const currentPage = pages.find(p => p.id === activeTab);
  const CurrentIcon = currentPage?.icon || FileText;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/super-admin/dashboard')}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Legal Pages Editor</h1>
              <p className="text-sm text-slate-500">Manage Privacy Policy, Terms of Service, and Cookie Policy</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {pages.map((page) => {
            const Icon = page.icon;
            const isActive = activeTab === page.id;
            return (
              <button
                key={page.id}
                onClick={() => handleTabChange(page.id)}
                data-testid={`tab-${page.id}`}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium whitespace-nowrap transition-all ${
                  isActive 
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' 
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {page.name}
              </button>
            );
          })}
        </div>

        {/* Page Info Card */}
        <div className={`bg-gradient-to-r ${currentPage?.color || 'from-indigo-500 to-purple-600'} rounded-2xl p-6 mb-6 text-white`}>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <CurrentIcon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-1">{currentPage?.name}</h2>
                <p className="text-white/80 text-sm">{currentPage?.description}</p>
                {pagesData[activeTab]?.last_updated && (
                  <p className="text-white/60 text-xs mt-2">
                    Last updated: {new Date(pagesData[activeTab].last_updated).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => handleViewPage(currentPage?.path)}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
              data-testid="view-page-btn"
            >
              <ExternalLink className="w-4 h-4" />
              View Live
            </button>
          </div>
        </div>

        {/* Editor Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (editMode) {
                  setEditedContent(pagesData[activeTab].content || '');
                }
                setEditMode(!editMode);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                editMode 
                  ? 'bg-slate-200 text-slate-700' 
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
              data-testid="toggle-edit-btn"
            >
              {editMode ? <Eye className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
              {editMode ? 'Preview' : 'Edit Content'}
            </button>
          </div>
          
          {editMode && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleReset}
                disabled={resetting}
                className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors font-medium disabled:opacity-50"
                data-testid="reset-btn"
              >
                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Reset to Default
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50"
                data-testid="save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </button>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : editMode ? (
            <div className="p-4">
              <p className="text-sm text-slate-500 mb-3">
                Edit the HTML content below. Use standard HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, etc.
              </p>
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full h-[600px] p-4 font-mono text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                placeholder="Enter HTML content..."
                data-testid="content-editor"
              />
            </div>
          ) : (
            <div className="p-8 md:p-12">
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {pagesData[activeTab]?.title}
              </h1>
              <div 
                className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-li:text-slate-600 prose-a:text-indigo-600"
                dangerouslySetInnerHTML={{ __html: editedContent || pagesData[activeTab]?.content || '<p>No content available.</p>' }}
                data-testid="content-preview"
              />
            </div>
          )}
        </div>

        {/* Page Status */}
        <div className="mt-8 bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">All Legal Pages Status</h3>
          <div className="space-y-3">
            {pages.map((page) => (
              <div key={page.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{page.name}</p>
                    <p className="text-sm text-slate-500">{page.path}</p>
                  </div>
                </div>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm rounded-full font-medium">
                  Published
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPagesEditor;
